import React from 'react';

import { NextPage } from 'next';

import { track } from '@hbf/analytics';

import {
  AnalyticsEvent,
  AnalyticsPageCategory,
} from '@kamernet/core/Analytics/types';
import { getListingAnalyticsPageName } from '@kamernet/core/Analytics/utilities/getListingAnalyticsPageName';
import {
  FindListingsResponse,
  FindMapDataResponse,
  FindMapListingResponse,
  SearchView,
} from '@kamernet/core/ApiClient/Kamernet';
import { getServerDataCache } from '@kamernet/core/DataCache/initializeServerDataCache';
import { PageLoaderErrorNotFound } from '@kamernet/core/Page';
import { definePageLoader } from '@kamernet/core/Page/definePageLoader';
import { TargetPageProps } from '@kamernet/core/Page/types';
import { PageLayout } from '@kamernet/core/PageLayout';
import { SEOLinks } from '@kamernet/core/PageLayout/components/SEOLinks';
import { ROUTES } from '@kamernet/core/Routing/routes';
import {
  getMainListingsSearchCategory,
  ListingsSearchCategory,
  ListingsSearchRouteParams,
} from '@kamernet/core/Routing/routes/ListingsSearch';

import { validateLocationSlugs } from '@kamernet/modules/GIS/utilities';
import { ListingsSearchAllFilters } from '@kamernet/modules/Search/types';
import {
  isStreetSearch,
  mapListingsSearchAllFiltersToListingRequest,
} from '@kamernet/modules/Search/utilities';

import { AllFiltersSection } from './components/AllFiltersSection';
import { ListingsGrid } from './components/ListingsGrid';
import { MapSearchViewSection } from './components/MapSearchViewSection';
import { useMetaTags } from './hooks';
import { useStoreAnonymousPSA } from './hooks/useStoreAnonymousPSA';
import { ListingsSearchProvider } from './ListingsSearchProvider';
import { mapRouteParamsToAllFilters } from './utilities/mapRouteParamsToAllFilters';

export interface ListingsSearchPageProps
  extends TargetPageProps<ListingsSearchRouteParams> {
  allFilters: ListingsSearchAllFilters;
  findListingsResponse: FindListingsResponse | null;
  findMapDataResponse: FindMapDataResponse | null;
  findMapListingsResponse: FindMapListingResponse | null;
  activeTenantsCount: number;
}

export const getServerSideProps = definePageLoader(
  async ({
    query,
    locale,
    apiClient,
    userProfile,
  }): Promise<ListingsSearchPageProps> => {
    const routeParams = ROUTES.ListingsSearchCity.parseRouteParams(
      query,
      locale,
    );

    const allFilters = mapRouteParamsToAllFilters(routeParams, {
      isAuthenticated: !!userProfile,
    });

    if (
      getMainListingsSearchCategory(allFilters.searchCategories) ===
      ListingsSearchCategory.Empty
    ) {
      throw new PageLoaderErrorNotFound();
    }

    const { cities, streets } = getServerDataCache();

    const locationSlugsValidationResult = validateLocationSlugs(
      allFilters.citySlug,
      allFilters.streetSlug,
      cities,
      streets,
    );

    if (!locationSlugsValidationResult.isValid) {
      throw new PageLoaderErrorNotFound();
    }

    allFilters.cityName = locationSlugsValidationResult.city?.name ?? null;
    allFilters.streetName = locationSlugsValidationResult.street?.name ?? null;
    const listingRequest =
      mapListingsSearchAllFiltersToListingRequest(allFilters);
    const isMapSearchView = allFilters.searchView === SearchView.Map;

    const hasMapMarker =
      isMapSearchView && !!allFilters.mapMarkerLat && !!allFilters.mapMarkerLng;

    const [findListingsResponse, findMapDataResponse, findMapListingsResponse] =
      await Promise.all([
        !hasMapMarker
          ? apiClient.listing.findListings({
              listingRequest,
            })
          : null,
        isMapSearchView
          ? apiClient.listing.findMapData({ listingRequest })
          : null,
        hasMapMarker
          ? apiClient.listing.findMapListings({
              findMapListingPayload: {
                PostalCodePoints: [
                  {
                    latitude: allFilters.mapMarkerLat,
                    Longitude: allFilters.mapMarkerLng,
                    count: 0,
                  },
                ],
              },
            })
          : null,
      ]);

    const activeTenantsCount =
      getServerDataCache().activeTenantsCount.find(
        item => item.cityId === locationSlugsValidationResult.city?.id,
      )?.total || 0;

    return {
      routeParams,
      allFilters,
      findListingsResponse,
      findMapDataResponse,
      findMapListingsResponse,
      activeTenantsCount,
      analytics: {
        name: getListingAnalyticsPageName(allFilters.searchCategories),
        category: AnalyticsPageCategory.SearchResults,
      },
    };
  },
);

const ListingsSearchPage: NextPage<ListingsSearchPageProps> = ({
  allFilters,
  findListingsResponse,
  findMapDataResponse,
  findMapListingsResponse,
  activeTenantsCount,
}) => {
  useStoreAnonymousPSA(allFilters);

  const { metaTitle, metaDescription } = useMetaTags(allFilters);

  React.useEffect(() => {
    if (allFilters.cityName && allFilters.searchView === SearchView.List) {
      const trackPayload = {
        query: allFilters.cityName,
        search_string: allFilters.cityName,
      };

      track(AnalyticsEvent.SearchWithCity, trackPayload);
    }
  }, [allFilters.cityName, allFilters.searchView]);

  const route = isStreetSearch(allFilters)
    ? ROUTES.ListingsSearchStreet
    : ROUTES.ListingsSearchCity;

  return (
    <ListingsSearchProvider
      allFilters={allFilters}
      findListingsResponse={findListingsResponse}
      findMapDataResponse={findMapDataResponse}
      findMapListingsResponse={findMapListingsResponse}
      activeTenantsCount={activeTenantsCount}
    >
      <PageLayout
        pageTitle={metaTitle}
        metaDescription={metaDescription}
        seoLinksByLocale={SEOLinks({
          route,
          routeParams: {
            searchCategories: allFilters.searchCategories,
            city: allFilters.citySlug,
            street: allFilters.streetSlug,
          },
        })}
        hideFooter={allFilters.searchView === SearchView.Map}
      >
        <PageLayout.MainRaw>
          <AllFiltersSection />
          {allFilters.searchView === SearchView.Map ? (
            <MapSearchViewSection />
          ) : (
            <ListingsGrid />
          )}
        </PageLayout.MainRaw>
      </PageLayout>
    </ListingsSearchProvider>
  );
};

export default ListingsSearchPage;
